import { faultySensors } from './constants';

export const isOutlier = (value, values) => {
  if (!Array.isArray(values) || values.length < 4) return false;
  const sorted = [...values].sort((a, b) => a - b);
  const q1 = sorted[Math.floor(sorted.length / 4)];
  const q3 = sorted[Math.floor((3 * sorted.length) / 4)];
  const iqr = q3 - q1;
  const lowerBound = q1 - (1.5 * iqr);
  const upperBound = q3 + (1.5 * iqr);
  return value < lowerBound || value > upperBound;
};

export const calculateAdjustedPM25 = (readings, sensorIds) => {
  if (!Array.isArray(readings) || readings.length === 0) return null;

  // Filter out invalid readings and readings from faulty sensors
  const validReadings = readings.filter((reading) => {
    if (!reading || typeof reading.pm25 !== 'number') return false;
    if (reading.pm25 <= 0) return false;
    if (reading.pm25 > 500) return false;
    if (faultySensors.has(Number(reading.sensorId))) return false;
    return true;
  }).map(reading => reading.pm25);

  if (validReadings.length === 0) return null;

  // Remove outliers using IQR method if we have enough readings
  const finalReadings = validReadings.length >= 4 
    ? validReadings.filter(value => !isOutlier(value, validReadings))
    : validReadings;

  if (finalReadings.length === 0) return null;

  const sum = finalReadings.reduce((acc, val) => acc + val, 0);
  return sum / finalReadings.length;
};

export const calculateActualPM25 = (readings) => {
  if (!Array.isArray(readings) || readings.length === 0) return null;

  const validReadings = readings.filter(reading => {
    if (!reading || typeof reading.pm25 !== 'number') return false;
    if (reading.pm25 <= 0) return false;
    if (reading.pm25 > 500) return false;
    return true;
  }).map(reading => reading.pm25);
  
  if (validReadings.length === 0) return null;

  return validReadings.reduce((sum, val) => sum + val, 0) / validReadings.length;
};

export const getActiveSensorCount = (sensorData) => {
  if (!sensorData?.data || !Array.isArray(sensorData.data)) return 0;
  
  const activeSensors = new Set(
    sensorData.data
      .filter(reading => {
        return reading?.sensorId && 
               reading?.pm25 && 
               reading.pm25 > 0 && 
               reading.pm25 <= 500 &&
               !faultySensors.has(Number(reading.sensorId));
      })
      .map(reading => reading.sensorId)
  );

  return activeSensors.size;
};

export const getLatestLocationReadings = (sensorData, locationId, locationSensors) => {
  if (!sensorData?.data || !Array.isArray(sensorData.data)) return {};
  
  const validReadings = sensorData.data
    .filter(reading => {
      const sensorInfo = locationSensors[reading.sensorId];
      return sensorInfo && 
             reading?.pm25 && 
             reading.pm25 > 0 && 
             reading.pm25 <= 500 &&
             !faultySensors.has(Number(reading.sensorId));
    });

  const latestReadings = {};
  validReadings.forEach(reading => {
    const sensorInfo = locationSensors[reading.sensorId];
    if (!sensorInfo) return;

    if (!latestReadings[reading.sensorId] || 
        new Date(reading.timestamp) > new Date(latestReadings[reading.sensorId].timestamp)) {
      latestReadings[reading.sensorId] = reading;
    }
  });

  return latestReadings;
};